import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { formatBriefTrackerDate } from '../brief-tracker/brief-tracker.helpers';
import { AdminSandboxUserService } from '../../services/admin-sandbox-user.service';
import {
  AdminSandboxUserLookupDto,
  emptySandboxAccess,
  SANDBOX_ACCESS_FLAG_DEFS,
  SandboxAccessFlagKey,
  SandboxCreatorSearchRow,
  SandboxUserAccessDto,
} from './sandbox-user.types';

@Component({
  selector: 'admin-tool-sandbox-user-config',
  templateUrl: './sandbox-user-config.html',
  styleUrl: './sandbox-user-config.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SandboxUserConfig {
  readonly flagDefs = SANDBOX_ACCESS_FLAG_DEFS;
  readonly formatDate = formatBriefTrackerDate;

  userIdInput = signal('');
  lookupLoading = signal(false);
  saveLoading = signal(false);
  sandboxToggleLoading = signal(false);
  lookupError = signal<string | null>(null);
  user = signal<AdminSandboxUserLookupDto | null>(null);
  access = signal<SandboxUserAccessDto>(emptySandboxAccess());

  creatorSearchInput = signal('');
  creatorSearchLoading = signal(false);
  creatorSearchError = signal<string | null>(null);
  creatorSearchResults = signal<SandboxCreatorSearchRow[]>([]);
  selectedCreatorIds = signal<string[]>([]);
  selectedCreatorLabels = signal<Record<string, string>>({});

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private adminSandboxUserService = inject(AdminSandboxUserService);
  private toastNotificationService = inject(ToastNotificationService);

  onUserIdInput(value: string): void {
    this.userIdInput.set(value.trim());
  }

  onCreatorSearchInput(value: string): void {
    this.creatorSearchInput.set(value);
  }

  runUserLookup(): void {
    const firebaseUid = this.userIdInput().trim();
    if (!firebaseUid) {
      void this.toastNotificationService.alert({
        title: 'Missing field',
        message: 'Enter a user Firebase UID.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.lookupLoading.set(true);
    this.lookupError.set(null);
    this.user.set(null);
    this.creatorSearchResults.set([]);
    this.creatorSearchError.set(null);

    this.adminSandboxUserService
      .getUser(firebaseUid)
      .pipe(
        switchMap((foundUser) => {
          if (!foundUser.sandbox_user) {
            return of({ user: foundUser, access: null as SandboxUserAccessDto | null });
          }
          return this.adminSandboxUserService
            .getSandboxAccess(firebaseUid)
            .pipe(
              switchMap((access) =>
                of({
                  user: { ...foundUser, sandboxAccess: access },
                  access,
                }),
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ user: foundUser, access }) => {
          this.lookupLoading.set(false);
          this.user.set(foundUser);
          const nextAccess = access ?? emptySandboxAccess();
          this.access.set(nextAccess);
          this.selectedCreatorIds.set([...(nextAccess.allowed_creators ?? [])]);
          this.selectedCreatorLabels.set({});
          this.cdr.markForCheck();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.lookupLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.lookupError.set(message);
          void this.toastNotificationService.alert({
            title: 'User lookup failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  isSandboxEnabled(): boolean {
    return !!this.user()?.sandbox_user;
  }

  onSandboxEnabledChange(enabled: boolean): void {
    const firebaseUid = this.user()?.firebaseUid;
    if (!firebaseUid) {
      return;
    }

    this.sandboxToggleLoading.set(true);
    this.adminSandboxUserService
      .setSandboxUserFlag(firebaseUid, enabled)
      .pipe(
        switchMap(() => {
          if (!enabled) {
            return of(null as SandboxUserAccessDto | null);
          }
          return this.adminSandboxUserService.getSandboxAccess(firebaseUid);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (access) => {
          this.sandboxToggleLoading.set(false);
          const current = this.user();
          if (!current) {
            return;
          }
          this.user.set({ ...current, sandbox_user: enabled, sandboxAccess: access });
          const nextAccess = access ?? emptySandboxAccess();
          this.access.set(nextAccess);
          this.selectedCreatorIds.set([...(nextAccess.allowed_creators ?? [])]);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: enabled ? 'Sandbox enabled' : 'Sandbox disabled',
            message: enabled
              ? 'User is now a sandbox user. Default permissions were created — review toggles below.'
              : 'Sandbox mode disabled for this user.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.sandboxToggleLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Sandbox toggle failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  isFlagEnabled(key: SandboxAccessFlagKey): boolean {
    return this.access()[key] === true;
  }

  onFlagChange(key: SandboxAccessFlagKey, enabled: boolean): void {
    this.access.update((current) => ({ ...current, [key]: enabled }));
    this.cdr.markForCheck();
  }

  runCreatorSearch(): void {
    const phrase = this.creatorSearchInput().trim();
    if (!phrase) {
      void this.toastNotificationService.alert({
        title: 'Missing field',
        message: 'Enter a creator name, email, or Firebase UID to search.',
        type: 'error',
        icon: 'error',
      });
      return;
    }

    this.creatorSearchLoading.set(true);
    this.creatorSearchError.set(null);

    this.adminSandboxUserService
      .searchCreators(phrase)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.creatorSearchLoading.set(false);
          const rows = (response?.data ?? []).map((row) => ({
            blazeFirebaseUid: row.blazeFirebaseUid,
            email: row.email ?? '',
            firstName: row.firstName ?? '',
            lastName: row.lastName ?? '',
          }));
          this.creatorSearchResults.set(rows);
          this.cdr.markForCheck();
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.creatorSearchLoading.set(false);
          const message = this.extractErrorMessage(err);
          this.creatorSearchError.set(message);
          void this.toastNotificationService.alert({
            title: 'Creator search failed',
            message,
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  isCreatorSelected(creatorId: string): boolean {
    return this.selectedCreatorIds().includes(creatorId);
  }

  onCreatorSelectionChange(creator: SandboxCreatorSearchRow, checked: boolean): void {
    const id = creator.blazeFirebaseUid;
    const label = this.creatorLabel(creator);

    if (checked) {
      if (!this.selectedCreatorIds().includes(id)) {
        this.selectedCreatorIds.update((ids) => [...ids, id]);
      }
      this.selectedCreatorLabels.update((labels) => ({ ...labels, [id]: label }));
    } else {
      this.selectedCreatorIds.update((ids) => ids.filter((rowId) => rowId !== id));
      this.selectedCreatorLabels.update((labels) => {
        const next = { ...labels };
        delete next[id];
        return next;
      });
    }
    this.cdr.markForCheck();
  }

  removeSelectedCreator(creatorId: string): void {
    this.selectedCreatorIds.update((ids) => ids.filter((id) => id !== creatorId));
    this.selectedCreatorLabels.update((labels) => {
      const next = { ...labels };
      delete next[creatorId];
      return next;
    });
    this.cdr.markForCheck();
  }

  selectedCreatorDisplayLabel(creatorId: string): string {
    return this.selectedCreatorLabels()[creatorId] ?? creatorId;
  }

  creatorLabel(creator: SandboxCreatorSearchRow): string {
    const name = `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim();
    if (name && creator.email) {
      return `${name} (${creator.email})`;
    }
    return name || creator.email || creator.blazeFirebaseUid;
  }

  saveAccess(): void {
    const firebaseUid = this.user()?.firebaseUid;
    if (!firebaseUid || !this.isSandboxEnabled()) {
      void this.toastNotificationService.alert({
        title: 'Cannot save',
        message: 'Enable sandbox mode for this user first.',
        type: 'warning',
        icon: 'warning',
      });
      return;
    }

    const payload: SandboxUserAccessDto = {
      ...this.access(),
      allowed_creators: [...this.selectedCreatorIds()],
    };

    this.saveLoading.set(true);
    this.adminSandboxUserService
      .updateSandboxAccess(firebaseUid, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.saveLoading.set(false);
          this.access.set(saved);
          this.selectedCreatorIds.set([...(saved.allowed_creators ?? [])]);
          this.cdr.markForCheck();
          void this.toastNotificationService.alert({
            title: 'Sandbox access saved',
            message: 'Permissions updated for this reviewer account.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: { error?: { message?: string | string[] }; message?: string }) => {
          this.saveLoading.set(false);
          void this.toastNotificationService.alert({
            title: 'Save failed',
            message: this.extractErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  private extractErrorMessage(err: {
    error?: { message?: string | string[] };
    message?: string;
  }): string {
    const nested = err?.error?.message;
    if (Array.isArray(nested)) {
      return nested.join(', ');
    }
    if (typeof nested === 'string' && nested.length) {
      return nested;
    }
    return err?.message ?? 'Request failed';
  }
}
